/**
 * Files Card Component
 *
 * Displays referral documents with download functionality.
 * Extracted from ReferralItem component for better modularity.
 *
 * @module components/dietician-dashboard/FilesCard
 */

import React, { useEffect, useState } from 'react';
import { useReferralDocuments } from '@/hooks/useReferralDocuments';
import { BodyText } from '@/components/ui/BodyText';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import type { ReferralDocument } from '@/services/domains/ReferralDomain';

interface FilesCardProps {
  referralId: string;
  className?: string;
}

/**
 * Files Card Component
 */
export const FilesCard: React.FC<FilesCardProps> = ({ referralId, className = '' }) => {
  const { documents, loading: documentsLoading, fetchDocuments, downloadDocument } = useReferralDocuments();
  const [documentsLoaded, setDocumentsLoaded] = useState(false);

  // Fetch documents when component mounts
  useEffect(() => {
    if (!documentsLoaded) {
      fetchDocuments(referralId);
      setDocumentsLoaded(true);
    }
  }, [referralId, fetchDocuments, documentsLoaded]);

  const handleDownloadDocument = async (document: ReferralDocument) => {
    await downloadDocument(document.id, document.original_filename);
  };

  return (
    <Card className={`p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <BodyText variant="secondary" size="sm" className="font-medium">
          Files:
        </BodyText>
        {documentsLoading && (
          <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        )}
      </div>

      {documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((document) => (
            <div key={document.id} className="flex items-center justify-between">
              <BodyText variant="muted" size="sm" className="truncate flex-1 mr-2">
                {document.original_filename}
              </BodyText>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDownloadDocument(document)}
                className="px-2 py-1"
              >
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download
              </Button>
            </div>
          ))}
        </div>
      ) : (
        !documentsLoading && (
          <BodyText variant="muted" size="sm">
            No documents available
          </BodyText>
        )
      )}
    </Card>
  );
};

export default FilesCard;